/**
 * Traffic › session detail (`/traffic/session/:id?t=…&path=…`) — one
 * session's ordered event timeline, reached from the sessions list on
 * the page-focused traffic view.
 *
 * `t` is the session's started_at (ClickHouse DateTime string) from
 * the sessions list — the events pipe needs it to bound its raw scan
 * to ±1 day, since the events sorting key has no session_uuid. A deep
 * link without `t` renders the not-found state rather than scanning.
 * `path` is only used for the "back to page" link.
 *
 * The meta header derives client-side from the first/last rows — no
 * separate summary endpoint. The session id appears only as truncated
 * monospace secondary text (product rule: no raw internal ids as
 * primary copy).
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { StatusChip } from '@/components/primitives/MetricTile';
import { formatSessionDuration, parseClickHouseUtc } from '@/components/rum/PageTrafficView';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Button } from '@/components/ui/button';
import { useActiveSiteUuid } from '@/lib/active-site';
import {
  type MetricBand,
  bandFill,
  bandText,
  clsBand,
  formatCls,
  formatMs,
  inpBand,
  lcpBand,
  networkLabel,
} from '@/lib/format';
import { type PixelSessionEventRow, usePixelSessionEvents } from '@/lib/queries/pixel';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  ArrowSquareOut,
  CaretDown,
  File as FileIcon,
  Sparkle,
  UsersThree,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, type ReactNode, useState } from 'react';

interface SessionSearch {
  /** started_at hint for the events pipe's bounded scan. */
  t?: string;
  /** page_path the user drilled in from — back-link only. */
  path?: string;
  /** `?raw=1` — show every beacon the pipe returned, including the ones
   *  the timeline normally folds away. Support/debugging escape hatch,
   *  deliberately undiscoverable: there's no control for it. */
  raw?: boolean;
}

export const Route = createFileRoute('/_app/traffic/session/$sessionId')({
  validateSearch: (search: Record<string, unknown>): SessionSearch => ({
    ...(typeof search.t === 'string' && search.t !== '' ? { t: search.t } : {}),
    ...(typeof search.path === 'string' && search.path !== '' ? { path: search.path } : {}),
    ...(search.raw === '1' || search.raw === true ? { raw: true } : {}),
  }),
  component: SessionPage,
});

function SessionPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const { sessionId } = Route.useParams();
  const { t, path, raw } = Route.useSearch();

  const events = usePixelSessionEvents(siteUuid, { session: sessionId, around: t });
  const rows = events.data ?? [];
  const first = rows[0];
  const last = rows[rows.length - 1];

  return (
    <SiteIsland section="Session" parent={{ label: 'Traffic', href: '/traffic' }}>
      {siteUuid && (
        <div className="space-y-6">
          {path && (
            <Link
              to="/traffic/page"
              search={{ path }}
              className="inline-flex items-center gap-1.5 text-xs text-[var(--color-mute)] transition-colors hover:text-[var(--color-ink-soft)]"
            >
              <ArrowLeft aria-hidden="true" size={12} />
              Back to {path}
            </Link>
          )}
          {rows.length > 0 && first && last ? (
            <>
              <SessionMeta rows={rows} first={first} last={last} sessionId={sessionId} />
              <SessionExperience rows={rows} />
              <SessionTimeline rows={rows} raw={raw ?? false} />
            </>
          ) : events.isPending && !!t ? (
            <div className="px-6 py-12 text-center text-xs text-[var(--color-mute)]">
              Loading session…
            </div>
          ) : (
            <EmptyState
              icon={UsersThree}
              title="Session not found"
              description="This session has no events we can read — it may have aged out of your plan's event retention, or the link is incomplete."
              action={
                <Button
                  variant="outline"
                  render={
                    <Link
                      to={path ? '/traffic/page' : '/traffic'}
                      search={path ? { path } : undefined}
                    />
                  }
                >
                  Back to Traffic
                </Button>
              }
            />
          )}
        </div>
      )}
    </SiteIsland>
  );
}

// ─── Shared section chrome ──────────────────────────────────────────
//
// Deliberately the same shapes the audit detail page uses (its local
// `MetricSection` / `Field`): a titled card with a one-line subtitle,
// and micro-labelled facts in the identity header. Sessions and audits
// are both "one record, examined" pages and should read as siblings.

function SessionSection({
  title,
  subtitle,
  aside,
  children,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
          {subtitle && <p className="text-xs text-[var(--color-mute)]">{subtitle}</p>}
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }): ReactElement | null {
  if (!value) return null;
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.06em]">{label}</dt>
      <dd className="truncate text-xs tabular-nums text-[var(--color-ink-soft)]" title={value}>
        {value}
      </dd>
    </div>
  );
}

function SessionMeta({
  rows,
  first,
  last,
  sessionId,
}: {
  rows: PixelSessionEventRow[];
  first: PixelSessionEventRow;
  last: PixelSessionEventRow;
  sessionId: string;
}): ReactElement {
  // Soft (SPA route change) pageviews count as views here too — the
  // timeline lists them, so counting only hard navigations reported
  // "Views 1" for a session the reader could see visiting three pages.
  const pageviews = rows.filter((r) => isPageviewKind(r.event_kind));
  const entry = pageviews[0]?.page_path ?? first.page_path;
  const exit = pageviews[pageviews.length - 1]?.page_path ?? last.page_path;
  const device = [first.form_factor, first.browser_family, first.os_family]
    .filter(Boolean)
    .join(' · ');
  const geo = [first.country_code, first.region_code].filter(Boolean).join(' · ');
  const source = first.referrer_host
    ? `${first.channel || 'Referral'} · ${first.referrer_host}`
    : first.channel;
  const utms = [first.utm_source, first.utm_medium, first.utm_campaign].filter(Boolean).join(' / ');
  const connection = [networkLabel(first.network_type), first.network_save_data ? 'Data Saver' : '']
    .filter(Boolean)
    .join(' · ');
  const journey = entry === exit ? entry : `${entry} → ${exit}`;
  const started = parseClickHouseUtc(first.timestamp);
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {/* What the visitor did leads; the session id stays a footnote
            (product rule: no raw internal ids as primary copy). */}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-sm font-medium text-[var(--color-ink)]" title={journey}>
              {journey}
            </h2>
            <span className="shrink-0 rounded-full border border-[var(--color-line-strong)] px-1.5 py-px text-[10px] font-medium text-[var(--color-mute)]">
              {pageviews.length} {pageviews.length === 1 ? 'view' : 'views'}
            </span>
          </div>
          <div className="mt-1 truncate text-sm text-[var(--color-mute)]">
            {started.toLocaleString()}
            <span className="ml-2 font-mono text-[10px] text-[var(--color-mute)]/60">
              {sessionId}
            </span>
          </div>
        </div>
        <dl className="flex flex-wrap items-start gap-x-5 gap-y-2 text-xs text-[var(--color-mute)]">
          <Field label="Duration" value={formatSessionDuration(first.timestamp, last.timestamp)} />
          <Field label="Visitor" value={[geo, device].filter(Boolean).join(' · ')} />
          <Field label="Connection" value={connection} />
          <Field label="Source" value={source} />
          <Field label="Campaign" value={utms} />
        </dl>
      </div>
    </section>
  );
}

// ─── Experience strip ───────────────────────────────────────────────

/**
 * The headline answer to "how did this visit actually feel", so the page
 * opens with a verdict instead of asking the reader to scan a timeline
 * and compare numbers themselves.
 *
 * Worst value across the session's page lifecycles, not an average: a
 * visit is remembered by its worst moment, and averaging a bad page
 * against three good ones hides exactly the thing worth finding.
 */
function SessionExperience({ rows }: { rows: PixelSessionEventRow[] }): ReactElement | null {
  const lifecycles = [...collectVitals(rows).values()];
  if (lifecycles.length === 0) return null;
  const worstOf = (pick: (v: PageVitals) => number | null): number | null =>
    lifecycles.reduce<number | null>((acc, v) => worst(acc, pick(v)), null);

  // Only the three Core Web Vitals get a tile. TTFB and FCP are
  // diagnostics for the same load LCP already reports — five tiles made
  // the reader weigh five numbers to reach one verdict, so they drop to
  // a footnote where they're still available without competing.
  const tiles = [
    { label: 'LCP', value: worstOf((v) => v.lcp), format: formatMs, band: lcpBand },
    { label: 'CLS', value: worstOf((v) => v.cls), format: formatCls, band: clsBand },
    { label: 'INP', value: worstOf((v) => v.inp), format: formatMs, band: inpBand },
  ].filter((t) => t.value != null);
  if (tiles.length === 0) return null;
  const ttfb = worstOf((v) => v.ttfb);
  const fcp = worstOf((v) => v.fcp);
  const footnote = [
    ttfb != null ? `TTFB ${formatMs(ttfb)}` : '',
    fcp != null ? `FCP ${formatMs(fcp)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <SessionSection
      title="Experience"
      subtitle={
        lifecycles.length === 1
          ? 'The Core Web Vitals this visitor measured on the page they viewed.'
          : `Worst value across the ${lifecycles.length} pages in this session.`
      }
    >
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]">
        {tiles.map((t) => {
          const band = t.band(t.value);
          return (
            <div
              key={t.label}
              className="rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] p-3"
            >
              <div className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block size-2 shrink-0 rounded-full"
                  style={{ background: bandFill(band) }}
                />
                <span className="truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  {t.label}
                </span>
              </div>
              <div className={cn('mt-1 text-lg font-semibold tabular-nums', bandText(band))}>
                {t.format(t.value)}
              </div>
            </div>
          );
        })}
      </div>
      {footnote && <p className="mt-2 text-[11px] text-[var(--color-mute)]">{footnote}</p>}
    </SessionSection>
  );
}

// ─── Timeline ───────────────────────────────────────────────────────

/** Hard and soft (SPA route change) pageviews both open a page lifecycle. */
function isPageviewKind(kind: string): boolean {
  return kind === 'pageview' || kind === 'pageview_soft';
}

function eventGlyph(kind: string): ReactElement {
  const cls = 'size-3.5 text-[var(--color-mute)]';
  if (isPageviewKind(kind)) return <FileIcon aria-hidden="true" className={cls} />;
  if (kind === 'outbound' || kind === 'outbound_click')
    return <ArrowSquareOut aria-hidden="true" className={cls} />;
  return <Sparkle aria-hidden="true" className={cls} />;
}

function eventLabel(e: PixelSessionEventRow): string {
  if (isPageviewKind(e.event_kind)) return e.page_path;
  if (e.outgoing_host) return e.outgoing_host;
  return e.event_name || e.event_kind;
}

function eventDetail(e: PixelSessionEventRow): string {
  if (!isPageviewKind(e.event_kind)) return '';
  const parts: string[] = [];
  if (e.engagement_ms > 0) parts.push(`${Math.round(e.engagement_ms / 1000)}s engaged`);
  if (e.scroll_depth_pct > 0) parts.push(`${Math.round(e.scroll_depth_pct)}% scrolled`);
  return parts.join(' · ');
}

interface PageVitals {
  lcp: number | null;
  cls: number | null;
  inp: number | null;
  ttfb: number | null;
  fcp: number | null;
  loafMs: number | null;
  loafHost: string;
}

const EMPTY_VITALS: PageVitals = {
  lcp: null,
  cls: null,
  inp: null,
  ttfb: null,
  fcp: null,
  loafMs: null,
  loafHost: '',
};

/** Page-lifecycle key — web-vitals beacons carry the same page_path +
 *  pageview_count_in_session as the pageview they measured, which is
 *  the only join between the two (beacon ids are per-event). */
function lifecycleKey(e: PixelSessionEventRow): string {
  return `${e.pageview_count_in_session}|${e.page_path}`;
}

/** Worst value wins when a metric reports twice in one lifecycle —
 *  every vital here is "lower is better", so a later, worse reading is
 *  the one the visitor actually lived with. */
function worst(current: number | null, incoming: number | null): number | null {
  if (incoming == null) return current;
  return Math.max(current ?? 0, incoming);
}

/** Folds the raw web-vitals beacons (event_kind 'visibility', one
 *  metric per row) into per-lifecycle vitals. */
function collectVitals(rows: PixelSessionEventRow[]): Map<string, PageVitals> {
  const map = new Map<string, PageVitals>();
  for (const e of rows) {
    if (e.event_kind !== 'visibility') continue;
    const key = lifecycleKey(e);
    const v = { ...(map.get(key) ?? EMPTY_VITALS) };
    v.lcp = worst(v.lcp, e.lcp);
    v.cls = worst(v.cls, e.cls);
    v.inp = worst(v.inp, e.inp);
    v.ttfb = worst(v.ttfb, e.ttfb);
    v.fcp = worst(v.fcp, e.fcp);
    // Attribute the longest task to whichever beacon reported the worst
    // one — the script host only means something alongside its duration.
    if (e.longest_loaf_ms != null && e.longest_loaf_ms > (v.loafMs ?? 0)) {
      v.loafMs = e.longest_loaf_ms;
      v.loafHost = e.loaf_script_host;
    }
    map.set(key, v);
  }
  return map;
}

function hasVitals(v: PageVitals | undefined): v is PageVitals {
  if (!v) return false;
  return v.lcp != null || v.cls != null || v.inp != null || v.ttfb != null || v.fcp != null;
}

// ─── Page delivery (Navigation Timing) ──────────────────────────────

/** DNS / connect / request / response are true phase durations, so they
 *  can share one proportional bar. `nav_dom_interactive_ms` and
 *  `nav_dom_complete_ms` are elapsed-from-navigation-start milestones —
 *  drawing them as segments would double-count the phases above, so
 *  they read as markers instead. */
const DELIVERY_PHASES = [
  { key: 'nav_dns_ms', label: 'DNS' },
  { key: 'nav_tcp_ms', label: 'Connect' },
  { key: 'nav_request_ms', label: 'Request' },
  { key: 'nav_response_ms', label: 'Response' },
] as const;

/** Only hard navigations carry Navigation Timing — SPA route changes and
 *  bfcache restores legitimately have none, so this collapses to null
 *  rather than rendering an empty shell inside a timeline row. */
function PageDelivery({ event }: { event: PixelSessionEventRow }): ReactElement | null {
  const phases = DELIVERY_PHASES.map((p): { label: string; ms: number | null } => ({
    label: p.label,
    ms: event[p.key],
  })).filter((p): p is { label: string; ms: number } => p.ms != null && p.ms > 0);
  const total = phases.reduce((sum, p) => sum + p.ms, 0);

  const notes: string[] = [];
  if (event.nav_dom_interactive_ms != null)
    notes.push(`Interactive ${formatMs(event.nav_dom_interactive_ms)}`);
  if (event.nav_dom_complete_ms != null)
    notes.push(`Complete ${formatMs(event.nav_dom_complete_ms)}`);
  if (event.bfcache_hit) notes.push('Restored from back/forward cache');
  if (event.nav_type === 'reload') notes.push('Reload');
  if (event.nav_redirect_count > 0)
    notes.push(`${event.nav_redirect_count} redirect${event.nav_redirect_count === 1 ? '' : 's'}`);

  if (total === 0 && notes.length === 0) return null;

  return (
    <div className="mt-1.5 max-w-md space-y-1">
      {total > 0 && (
        <div
          className="flex h-1 w-full overflow-hidden rounded-full bg-[var(--color-line)]"
          title={`${formatMs(total)} before the browser could start rendering — ${phases
            .map((p) => `${p.label} ${formatMs(p.ms)}`)
            .join(' · ')}`}
        >
          {phases.map((p, i) => (
            <span
              key={p.label}
              className="bg-[var(--color-accent-brand)]"
              style={{ width: `${(p.ms / total) * 100}%`, opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      )}
      {notes.length > 0 && (
        <div className="text-[10px] text-[var(--color-mute)]">{notes.join(' · ')}</div>
      )}
    </div>
  );
}

/**
 * Beacons that exist for the data plane, not for the reader.
 *
 * `visibility` carries the web vitals, which render as the readout on
 * the pageview they measured rather than as rows of their own.
 * `pageend` is the pagehide flush that closes a page lifecycle — it
 * carries no metric at all, so on a three-page session it added three
 * rows saying nothing and broke the page-to-page rhythm the timeline
 * is meant to tell.
 *
 * Both are still fetched and still fold into the vitals, so `?raw=1`
 * costs nothing extra to honour.
 */
const FOLDED_EVENT_KINDS = new Set(['visibility', 'pageend']);

/** True when the row has Navigation Timing worth unfolding. Mirrors the
 *  condition `PageDelivery` uses to decide it has something to draw, so
 *  a chevron never opens onto nothing. */
function hasDelivery(e: PixelSessionEventRow): boolean {
  if (!isPageviewKind(e.event_kind)) return false;
  const phases = DELIVERY_PHASES.some((p) => (e[p.key] ?? 0) > 0);
  return (
    phases ||
    e.nav_dom_interactive_ms != null ||
    e.nav_dom_complete_ms != null ||
    e.bfcache_hit === 1 ||
    e.nav_type === 'reload' ||
    e.nav_redirect_count > 0
  );
}

/**
 * The always-visible line for one timeline row: what, how engaged, how
 * it felt, when — plus a chevron when there's detail underneath.
 *
 * The band arrives as a chip rather than five numbers. Five numbers per
 * row is what the sessions list used to do, and the same argument
 * applies harder here: the Experience strip above already prints the
 * session's worst values, so repeating them per page turned the page
 * into a wall of amber.
 */
function RowHeader({
  label,
  detail,
  band,
  vitalsTitle,
  at,
  expandable,
  isOpen,
  onToggle,
}: {
  label: string;
  detail: string;
  band: MetricBand;
  /** This page's exact vitals — the numbers the chip summarises. */
  vitalsTitle: string;
  at: Date;
  expandable: boolean;
  isOpen: boolean;
  onToggle: () => void;
}): ReactElement {
  const body = (
    <>
      <span className="truncate text-xs font-medium text-[var(--color-ink)]" title={label}>
        {label}
      </span>
      {detail && <span className="shrink-0 text-[11px] text-[var(--color-mute)]">{detail}</span>}
      {band !== 'unknown' && (
        <span className="shrink-0" title={vitalsTitle}>
          <StatusChip band={band} />
        </span>
      )}
      <span
        className="ml-auto shrink-0 pl-3 text-[11px] tabular-nums text-[var(--color-mute)]"
        title={at.toLocaleString()}
      >
        {at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
      {expandable && (
        <CaretDown
          aria-hidden="true"
          size={11}
          className={cn(
            'shrink-0 text-[var(--color-mute)] transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      )}
    </>
  );
  if (!expandable) {
    return <div className="flex min-w-0 items-center gap-3">{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex min-w-0 items-center gap-3 rounded text-left transition-colors hover:text-[var(--color-ink)]"
    >
      {body}
    </button>
  );
}

const BAND_RANK: Record<MetricBand, number> = {
  unknown: -1,
  good: 0,
  'needs-improvement': 1,
  poor: 2,
};

/** A page lifecycle's verdict — worst of the three Core Web Vitals it
 *  reported, matching how the sessions list summarises a whole visit. */
/** This page's vitals as one line, for the chip's tooltip. */
function vitalsList(v: PageVitals | undefined): string {
  if (!v) return '';
  return [
    v.lcp != null ? `LCP ${formatMs(v.lcp)}` : '',
    v.cls != null ? `CLS ${formatCls(v.cls)}` : '',
    v.inp != null ? `INP ${formatMs(v.inp)}` : '',
    v.ttfb != null ? `TTFB ${formatMs(v.ttfb)}` : '',
    v.fcp != null ? `FCP ${formatMs(v.fcp)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

function vitalsBand(v: PageVitals | undefined): MetricBand {
  if (!v) return 'unknown';
  const bands: MetricBand[] = [];
  if (v.lcp != null) bands.push(lcpBand(v.lcp));
  if (v.cls != null) bands.push(clsBand(v.cls));
  if (v.inp != null) bands.push(inpBand(v.inp));
  if (bands.length === 0) return 'unknown';
  return bands.reduce((worst, b) => (BAND_RANK[b] > BAND_RANK[worst] ? b : worst), 'good');
}

function SessionTimeline({
  rows,
  raw,
}: {
  rows: PixelSessionEventRow[];
  raw: boolean;
}): ReactElement {
  const vitals = collectVitals(rows);
  const visible = raw ? rows : rows.filter((e) => !FOLDED_EVENT_KINDS.has(e.event_kind));

  // The page worth reading is opened for you; the rest stay one line
  // until asked. Printing every page's five vitals put twenty coloured
  // numbers on one screen, most of them restating the Experience strip
  // directly above — the reader had to compare them to find the one
  // page that was actually bad.
  const worstKey = visible.reduce<{ key: string; rank: number }>(
    (acc, e, i) => {
      if (!isPageviewKind(e.event_kind)) return acc;
      const rank = BAND_RANK[vitalsBand(vitals.get(lifecycleKey(e)))];
      return rank > acc.rank ? { key: `${e.timestamp}-${i}`, rank } : acc;
    },
    { key: '', rank: 0 },
  ).key;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() =>
    worstKey ? new Set([worstKey]) : new Set(),
  );
  const toggle = (key: string): void =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <SessionSection
      title="Timeline"
      subtitle="Every page this visitor opened, in order."
      aside={
        /* Without this, a shared ?raw=1 link just looks like a buggy
           timeline full of rows that say "pageend". */
        raw ? (
          <span className="shrink-0 text-[10px] text-[var(--color-mute)]">
            every beacon, unfolded
          </span>
        ) : undefined
      }
    >
      <ul>
        {visible.map((e, i) => {
          const pageVitals = isPageviewKind(e.event_kind) ? vitals.get(lifecycleKey(e)) : undefined;
          const at = parseClickHouseUtc(e.timestamp);
          const key = `${e.timestamp}-${i}`;
          // Outbound clicks and custom events have nothing underneath —
          // no chevron, no click target, no disappointment.
          const canExpand = hasVitals(pageVitals) || hasDelivery(e);
          const isOpen = canExpand && expanded.has(key);
          return (
            <li
              key={`${e.timestamp}-${i}`}
              className="relative flex items-start gap-3 pb-4 last:pb-0"
            >
              {/* Rail connecting the event dots. */}
              {i < visible.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute top-5 left-[7px] bottom-0 w-px bg-[var(--color-line-soft)]"
                />
              )}
              <span className="relative mt-0.5 flex size-4 shrink-0 items-center justify-center">
                {eventGlyph(e.event_kind)}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {/* One line per page: what, how engaged, how it felt,
                    when. The clock sits right — it's the one fact a
                    timeline owes the reader, and it anchors the row. */}
                <RowHeader
                  label={eventLabel(e)}
                  detail={eventDetail(e)}
                  band={vitalsBand(pageVitals)}
                  vitalsTitle={vitalsList(pageVitals)}
                  at={at}
                  expandable={canExpand}
                  isOpen={isOpen}
                  onToggle={() => toggle(key)}
                />
                {isOpen && (
                  <div className="flex flex-col gap-1 pb-1">
                    {/* No vitals readout here: the strip above already
                        gives the session's numbers and the chip gives
                        this page's verdict, so repeating five figures
                        per row said the same thing a third time.
                        Expanding is for the *why* — where the time
                        went. Exact per-page values live on the chip. */}
                    {isPageviewKind(e.event_kind) && <PageDelivery event={e} />}
                    {pageVitals?.loafMs != null && (
                      <div className="text-[10px] text-[var(--color-mute)]">
                        Longest task {formatMs(pageVitals.loafMs)}
                        {pageVitals.loafHost && ` — ${pageVitals.loafHost}`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </SessionSection>
  );
}
